import './App.css';

import { Alert, Box, Button, CircularProgress, Container } from '@mui/material';
import { useEffect, useState, lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';

import AuthWrapper from './components/Auth/AuthWrapper';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';
import Header from './components/Header';
import { I18nProvider, useI18n } from './contexts/I18nContext';
import { initializeDatabase } from './db/init';
import useAuthHydration from './hooks/useAuthHydration';
import { useProductScopingStore } from './stores/productScopingStore';

// Lazy imports
const About = lazy(() => import('./components/About'));
const AuthPage = lazy(() => import('./components/Auth/AuthPage'));
const OAuthCallback = lazy(() => import('./components/Auth/OAuthCallback'));
const DemoDashboard = lazy(() => import('./components/Dashboard/DemoDashboard'));
const ProjectDashboard = lazy(() => import('./components/Dashboard/ProjectDashboard'));
const DocDashboard = lazy(() => import('./components/DoC/DocDashboard'));
const DocForm = lazy(() => import('./components/DoC/DocForm'));
const DeclarationOfConformityPage = lazy(() => import('./components/Features/DeclarationOfConformity'));
const FAQ = lazy(() => import('./components/FAQ'));
const Landing = lazy(() => import('./components/Landing'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Pricing = lazy(() => import('./components/Pricing'));
const Insights = lazy(() => import('./components/Insights'));
const Resources = lazy(() => import('./components/Resources'));
const GDPRGuide = lazy(() => import('./components/GDPRGuide'));
const AMLDGuide = lazy(() => import('./components/AMLDGuide'));
const WhistleblowerGuide = lazy(() => import('./components/WhistleblowerGuide'));
const SanctionsGuide = lazy(() => import('./components/SanctionsGuide'));
const Articles = lazy(() => import('./components/Articles'));
const SMECompliance = lazy(() => import('./components/SMECompliance'));
const ProductScoping = lazy(() => import('./components/ProductScoping/ProductScoping'));
const Results = lazy(() => import('./components/Results'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const FoundingPartnerProgram = lazy(() => import('./components/FoundingPartnerProgram'));
const UserProfile = lazy(() => import('./components/User/User'));

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="error" onClose={resetErrorBoundary}>
        <strong>Something went wrong:</strong> {error.message}
      </Alert>
    </Container>
  );
}

function SessionRestorationBanner() {
  const { answers, reset } = useProductScopingStore();
  const { t } = useI18n();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if there are saved answers and we're on the home page
    const hasSavedAnswers = Object.keys(answers).length > 0;
    const isOnHomePage = window.location.pathname === '/';

    if (hasSavedAnswers && isOnHomePage) {
      setShowBanner(true);
    }
  }, [answers]);

  const handleResumeSession = () => {
    setShowBanner(false);
    // The product scoping will automatically load the saved state
  };

  const handleStartOver = () => {
    reset();
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button color="inherit" size="small" onClick={handleResumeSession}>
            {t('session.restoration.resume')}
          </Button>
          <Button color="inherit" size="small" onClick={handleStartOver}>
            {t('session.restoration.startOver')}
          </Button>
        </Box>
      }
    >
      <strong>{t('session.restoration.title')}</strong>{' '}
      {t('session.restoration.message').replace('{count}', Object.keys(answers).length.toString())}
    </Alert>
  );
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Box>
  );
}

function AppContent() {
  const { language, setLanguage } = useI18n();
  useAuthHydration();

  useEffect(() => {
    // Initialize database on app start
    initializeDatabase().catch(console.error);
  }, []);

  const handleLanguageChange = (language: string) => {
    setLanguage(language);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Helmet>
        <title>Conformity Core</title>
        <meta
          name="description"
          content="Your central platform for navigating regulations and managing machinery compliance from start to finish."
        />
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header currentLanguage={language} onLanguageChange={handleLanguageChange} />

        <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
          <Container maxWidth="lg">
            <SessionRestorationBanner />
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/demo" element={<DemoDashboard />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/founding-partner-program" element={<FoundingPartnerProgram />} />
                <Route path="/path" element={<ProductScoping />} />
                <Route path="/results" element={<Results />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/gdpr-guide" element={<GDPRGuide />} />
                <Route path="/resources/amld-guide" element={<AMLDGuide />} />
                <Route path="/resources/whistleblower-guide" element={<WhistleblowerGuide />} />
                <Route path="/resources/sanctions-guide" element={<SanctionsGuide />} />
                <Route path="/resources/articles" element={<Articles />} />
                <Route path="/resources/sme-compliance" element={<SMECompliance />} />
                <Route
                  path="/features/declaration-of-conformity"
                  element={<DeclarationOfConformityPage />}
                />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                {/* Alias route for users expecting /login */}
                <Route path="/login" element={<AuthPage />} />
                <Route path="/create-project" element={<ProductScoping />} />
                <Route
                  path="/dashboard"
                  element={
                    <AuthWrapper>
                      <ProjectDashboard />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/projects/:projectId/docs"
                  element={
                    <AuthWrapper>
                      <DocDashboard />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/projects/:projectId/docs/new"
                  element={
                    <AuthWrapper>
                      <DocForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/projects/:projectId/docs/:docId/edit"
                  element={
                    <AuthWrapper>
                      <DocForm />
                    </AuthWrapper>
                  }
                />
                <Route
                  path="/user"
                  element={
                    <AuthWrapper>
                      <UserProfile />
                    </AuthWrapper>
                  }
                />
              </Routes>
            </Suspense>
          </Container>
        </Box>
        <Footer />
        <CookieConsent />
      </Box>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
}

export default App;
